import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {
  CategoryCode,
  Coverage,
  Duration,
  ENVIRONMENT,
  EnvironmentShared,
  JobRiskClass,
  Protection,
  ProtectionConfigInterface,
  ProtectionRequestInterface,
  ProviderId,
  Rider,
  RiderRequestInterface,
  Sex,
  Version,
  WithOption,
} from '@emma-labs/shared/typescript';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {appendSearchParam, defaultOptions, handleHttpError, responseToObject} from '../http-utils';

@Injectable({
  providedIn: 'root',
})
export class ProtectionApiService {
  basePath: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(ENVIRONMENT) private readonly environment: EnvironmentShared,
  ) {
    this.basePath = `${environment.msProposalApiBase}`;
  }

  getProtections(
    sessionId: string,
    protectionRequest: ProtectionRequestInterface,
    withRenewalPrices = false,
    allowPartialSuccess = false,
  ): Observable<{protections: Protection[]}> {
    const body = protectionRequest;
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/protections`);
    if (withRenewalPrices) {
      appendSearchParam(url, 'withOptions', WithOption.RENEWAL_PRICES);
    }
    if (allowPartialSuccess) {
      appendSearchParam(url, 'withOptions', WithOption.ALLOW_PARTIAL_SUCCESS);
    }
    return this.http.post(url.toString(), body, options).pipe(
      map((response) => responseToObject<{protections: Protection[]}>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  getRiders(sessionId: string, riderRequestInterface: RiderRequestInterface): Observable<{riders: Rider[]}> {
    const body = riderRequestInterface;
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/protections/riders`);
    return this.http.post(url.toString(), body, options).pipe(
      map((response) => responseToObject<{riders: Rider[]}>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  static buildProtectionConfig(
    ratesVersion: Version,
    providerId: ProviderId,
    categoryCodes: CategoryCode[],
    age: number,
    sex: Sex,
    smoker: boolean,
    coverage: Coverage,
    extraConfig?: {
      surchargeRate?: number;
      jobRiskClass?: JobRiskClass;
    },
  ): ProtectionConfigInterface {
    return {
      providerConfig: {
        id: providerId,
        categoryCodes: categoryCodes,
        jobRiskClass: extraConfig?.jobRiskClass,
      },
      age,
      sex,
      smoker,
      lifeInsuranceConfigs: Object.keys(coverage.lifeInsurances).map((duration) => ({
        duration: duration as Duration,
        coverage: coverage.lifeInsurances[duration as Duration] as number,
      })),
      riders: coverage.riders,
      surchargeRate: extraConfig?.surchargeRate,
      ratesVersion,
    };
  }
}
